import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, CheckCircle, XCircle, Info } from 'lucide-react';
import { useDispatch } from 'react-redux';
import FeedbackPopover from './FeedbackPopover';
import { EntryFeedback } from '../store/conversationSlice';
import { updateLandmineFeedback } from '../store/slices/landmineSlice';
import { apiService } from '../services/apiService';
import { sessionManager } from '../services/sessionManager';
import { useToast } from '../contexts/ToastContext';

interface LandmineAlertType {
  trigger: string;
  matched_text: string;
  matched_phrase: string;  // The phrase that semantically matched
  similarity_score: number;  // Confidence 0.0-1.0 (e.g., 0.85 = 85% match)
  semantic_match: boolean;  // True for LLM match
  landmine_type?: 'legal_risk' | 'detractor';  // Type of landmine
  category?: string;  // Only for legal_risk type
  priority: 'high' | 'medium' | 'low';
  can_say?: Array<{
    point: string;
    context?: string;
  }>;
  cannot_say?: Array<{
    point: string;
    reason?: string;
    severity?: 'legal_risk' | 'compliance_risk' | 'brand_risk' | 'general';
  }>;
  quick_tips?: string;
  detected_at: string;
  speaker: string;
  feedback?: EntryFeedback;
}

interface LandmineAlertProps {
  alert: LandmineAlertType;
  onDismiss: () => void;
}

export const LandmineAlert: React.FC<LandmineAlertProps> = ({ alert, onDismiss }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [autoCollapse, setAutoCollapse] = useState<NodeJS.Timeout | null>(null);

  const handleFeedbackSubmit = async (rating: 'up' | 'down', reason: string) => {
    const roomId = sessionManager.getRoomId();

    // Update Redux immediately so UI reflects the choice even if persist fails
    const feedback: EntryFeedback = {
      rating,
      reason: reason || undefined,
      submittedAt: new Date().toISOString()
    };
    dispatch(updateLandmineFeedback({ detected_at: alert.detected_at, feedback }));
    showToast('Feedback recorded');

    if (!roomId) return;

    try {
      await apiService.submitEntryFeedback(roomId, {
        entryType: 'landmine',
        entryTimestamp: alert.detected_at,
        rating,
        reason
      });
    } catch (err) {
      console.warn('[LandmineAlert] Entry feedback persist failed:', err);
    }
  };

  useEffect(() => {
    // Auto-expand legal risk alerts by default (they have canSay/cannotSay guidelines)
    // Detractor alerts don't need expansion - they show corrective phrase directly
    if (alert.landmine_type !== 'detractor') {
      setExpanded(true);
    }

    return () => {
      if (autoCollapse) {
        clearTimeout(autoCollapse);
      }
    };
  }, [alert.landmine_type]);

  const getPriorityColor = () => {
    switch (alert.priority) {
      case 'high':
        return 'bg-red-100 border-red-500 text-red-900';
      case 'medium':
        return 'bg-orange-100 border-orange-500 text-orange-900';
      case 'low':
        return 'bg-yellow-100 border-yellow-500 text-yellow-900';
      default:
        return 'bg-gray-100 border-gray-500 text-gray-900';
    }
  };

  const getPriorityIcon = () => {
    switch (alert.priority) {
      case 'high':
        return <AlertTriangle className="w-6 h-6 text-red-600 animate-pulse" />;
      case 'medium':
        return <AlertTriangle className="w-6 h-6 text-orange-600" />;
      case 'low':
        return <Info className="w-6 h-6 text-yellow-600" />;
      default:
        return <Info className="w-6 h-6 text-gray-600" />;
    }
  };

  const getSeverityBadge = (severity?: string) => {
    if (!severity) return null;

    const colors = {
      legal_risk: 'bg-red-600 text-white',
      compliance_risk: 'bg-orange-600 text-white',
      brand_risk: 'bg-blue-600 text-white',
      general: 'bg-gray-600 text-white'
    };

    const labels = {
      legal_risk: 'Legal Risk',
      compliance_risk: 'Compliance Risk',
      brand_risk: 'Brand Risk',
      general: 'General'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[severity as keyof typeof colors] || colors.general}`}>
        {labels[severity as keyof typeof labels] || severity}
      </span>
    );
  };

  const getConfidenceBadge = () => {
    const score = alert.similarity_score;
    if (score === undefined || score === null) return null;

    const percentage = Math.round(score * 100);
    let colorClass = 'bg-green-600 text-white';

    if (percentage < 85) {
      colorClass = 'bg-yellow-600 text-white';
    } else if (percentage >= 95) {
      colorClass = 'bg-green-700 text-white';
    }

    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colorClass}`} title="Semantic match confidence">
        {percentage}% match
      </span>
    );
  };

  return (
    <div className={`border-l-4 p-4 mb-4 rounded-r-lg shadow-lg transition-all duration-300 ${getPriorityColor()}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start flex-1">
          {getPriorityIcon()}
          <div className="ml-3 flex-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">
                  Landmine Detected: "{alert.trigger}"
                </h3>
                {getConfidenceBadge()}
              </div>
              <div className="flex items-center gap-1 ml-4">
                <FeedbackPopover
                  currentFeedback={alert.feedback}
                  onSubmit={handleFeedbackSubmit}
                />
                <button
                  onClick={onDismiss}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Show matched phrase if different from trigger */}
            {alert.matched_phrase && alert.matched_phrase !== alert.trigger && (
              <p className="text-sm mt-1 text-gray-600 italic">
                Matched phrase: "{alert.matched_phrase}"
              </p>
            )}

            <p className="text-sm mt-1 text-gray-700">
              Speaker: <span className="font-medium">{alert.speaker}</span>
              {alert.landmine_type === 'detractor' ? (
                <> | <span className="font-medium text-purple-600">Detractor Phrase</span></>
              ) : alert.category ? (
                <> | Category: <span className="font-medium">{alert.category}</span></>
              ) : null}
            </p>

            {/* For Detractor type - show corrective phrase prominently */}
            {alert.landmine_type === 'detractor' && alert.quick_tips && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center mb-1">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <h4 className="font-semibold text-green-800">Say This Instead:</h4>
                </div>
                <p className="text-green-900 font-medium pl-7">"{alert.quick_tips}"</p>
              </div>
            )}

            {/* For Legal Risk type - show quick tips if available */}
            {alert.landmine_type !== 'detractor' && alert.quick_tips && (
              <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-800">
                <strong>Quick Tip:</strong> {alert.quick_tips}
              </div>
            )}

            {/* Only show expand button for Legal Risk type with guidelines */}
            {alert.landmine_type !== 'detractor' && ((alert.can_say && alert.can_say.length > 0) || (alert.cannot_say && alert.cannot_say.length > 0)) && (
              <button
                onClick={() => {
                  setExpanded(!expanded);
                  if (autoCollapse) {
                    clearTimeout(autoCollapse);
                    setAutoCollapse(null);
                  }
                }}
                className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                {expanded ? 'Hide Details' : 'Show Guidelines'}
              </button>
            )}

            {expanded && alert.landmine_type !== 'detractor' && (
              <div className="mt-4 space-y-4 animate-fadeIn">
                {/* Can Say Section */}
                {alert.can_say && alert.can_say.length > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      <h4 className="font-semibold text-green-900">You CAN Say:</h4>
                    </div>
                    <ul className="space-y-2">
                      {alert.can_say.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <div>
                            <p className="text-sm text-green-900">{item.point}</p>
                            {item.context && (
                              <p className="text-xs text-green-700 italic mt-1">
                                Context: {item.context}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Cannot Say Section */}
                {alert.cannot_say && alert.cannot_say.length > 0 && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <div className="flex items-center mb-2">
                      <XCircle className="w-5 h-5 text-red-600 mr-2" />
                      <h4 className="font-semibold text-red-900">You CANNOT Say:</h4>
                    </div>
                    <ul className="space-y-2">
                      {alert.cannot_say.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-red-600 mr-2">✗</span>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-red-900">{item.point}</p>
                              {getSeverityBadge(item.severity)}
                            </div>
                            {item.reason && (
                              <p className="text-xs text-red-700 italic mt-1">
                                Reason: {item.reason}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Matched Text */}
                <div className="text-xs text-gray-600 mt-2">
                  <strong>Detected in:</strong> "{alert.matched_text}..."
                </div>
              </div>
            )}

            {/* Show matched text for detractor type without expand */}
            {alert.landmine_type === 'detractor' && (
              <div className="text-xs text-gray-500 mt-2">
                <strong>Detected in:</strong> "{alert.matched_text}..."
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Container component to manage multiple alerts
interface LandmineAlertsContainerProps {
  alerts: LandmineAlertType[];
  onDismiss: (index: number) => void;
}

export const LandmineAlertsContainer: React.FC<LandmineAlertsContainerProps> = ({ 
  alerts, 
  onDismiss 
}) => {
  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-20 right-4 w-96 max-h-[80vh] overflow-y-auto z-50">
      <div className="space-y-2">
        {alerts.map((alert, index) => (
          <LandmineAlert
            key={`${alert.trigger}-${alert.detected_at}-${index}`}
            alert={alert}
            onDismiss={() => onDismiss(index)}
          />
        ))}
      </div>
    </div>
  );
};